import { useEffect, useRef, useState } from "react";

const STREET_VIEW_TIMEOUT_MS = 8000;
const STREET_VIEW_BATCH_SIZE = 4;

export type PropertyImageMap = Record<string, string | null>;

export interface PropertyLike {
  property_id: string;
  address?:
    | string
    | {
        full_address?: string | null;
        line1?: string | null;
        latitude?: number | string | null;
        longitude?: number | string | null;
      };
  streetAddress?: string;
  cityStateZip?: string;
  rawDetails?: unknown;
}

/**
 * Fetches street view image metadata for given coordinates or address.
 * Returns the Google Static Street View URL if valid imagery exists, or null if unresolvable.
 */
export const getPropertyStreetView = async (
  lat?: string | number,
  lng?: string | number,
  fullAddressEncoded?: string,
  signal?: AbortSignal
): Promise<string | null> => {
  const apiKey =
    import.meta.env.VITE_GOOGLE_MAPS_API_KEY ||
    import.meta.env.VITE_GOOGLE_MAPS_API ||
    "AIzaSyATeOEAPWfZ_qiHKjJ0je_iW_dw1tf_4Kk";

  try {
    const cleanLat = lat !== undefined && lat !== null ? String(lat).trim() : "";
    const cleanLng = lng !== undefined && lng !== null ? String(lng).trim() : "";
    const hasCoords =
      cleanLat !== "" &&
      cleanLng !== "" &&
      cleanLat !== "0" &&
      cleanLng !== "0" &&
      cleanLat !== "undefined" &&
      cleanLng !== "undefined";

    const decodedAddress = fullAddressEncoded ? decodeURIComponent(fullAddressEncoded).trim() : "";

    const primaryLocation = hasCoords ? `${cleanLat},${cleanLng}` : decodedAddress;

    if (!primaryLocation) return null;

    const metadataUrl = `https://maps.googleapis.com/maps/api/streetview/metadata?location=${encodeURIComponent(
      primaryLocation
    )}&key=${apiKey}`;

    const response = await fetch(metadataUrl, { signal });
    if (!response.ok) return null;

    const data = await response.json();
    if (data && data.status === "OK") {
      return `https://maps.googleapis.com/maps/api/streetview?size=600x400&location=${encodeURIComponent(
        primaryLocation
      )}&key=${apiKey}&fov=90`;
    }

    // If coordinate search yielded ZERO_RESULTS, retry metadata search using full text address
    if (hasCoords && decodedAddress && data?.status !== "OK") {
      const addrMetadataUrl = `https://maps.googleapis.com/maps/api/streetview/metadata?location=${encodeURIComponent(
        decodedAddress
      )}&key=${apiKey}`;
      const addrResponse = await fetch(addrMetadataUrl, { signal });
      if (addrResponse.ok) {
        const addrData = await addrResponse.json();
        if (addrData && addrData.status === "OK") {
          return `https://maps.googleapis.com/maps/api/streetview?size=600x400&location=${encodeURIComponent(
            decodedAddress
          )}&key=${apiKey}&fov=90`;
        }
      }
    }

    return null;
  } catch (error) {
    if ((error as Error)?.name !== "AbortError") {
      console.error("getPropertyStreetView() error:", error);
    }
    return null;
  }
};

/**
 * One lookup per property for the life of the app process, shared across components.
 * A resolved `null` means "no imagery, or the lookup timed out"; card falls back to placeholder.
 */
const streetViewLookups = new Map<string, Promise<string | null>>();

export const lookupStreetView = (property: PropertyLike): Promise<string | null> => {
  const id = property.property_id;
  if (!id) return Promise.resolve(null);

  const cached = streetViewLookups.get(id);
  if (cached) return cached;

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), STREET_VIEW_TIMEOUT_MS);

  let lat: string | number | undefined;
  let lng: string | number | undefined;
  let fullAddress = "";

  if (typeof property.address === "object" && property.address !== null) {
    lat = property.address.latitude ?? undefined;
    lng = property.address.longitude ?? undefined;
    fullAddress = property.address.full_address || property.address.line1 || "";
  } else if (typeof property.address === "string") {
    fullAddress = property.address;
  }

  if (!lat && property.rawDetails) {
    const raw = property.rawDetails as Record<string, unknown>;
    const propInfo = raw.propertyInfo as Record<string, unknown> | undefined;
    const propAddr = propInfo?.address as Record<string, unknown> | undefined;

    lat =
      (propInfo?.latitude as number | string | undefined) ||
      (propAddr?.latitude as number | string | undefined) ||
      (raw.latitude as number | string | undefined);
    lng =
      (propInfo?.longitude as number | string | undefined) ||
      (propAddr?.longitude as number | string | undefined) ||
      (raw.longitude as number | string | undefined);
  }

  if (!fullAddress) {
    fullAddress = [property.streetAddress, property.cityStateZip].filter(Boolean).join(", ");
  }

  const request = getPropertyStreetView(
    lat,
    lng,
    fullAddress ? encodeURIComponent(fullAddress) : "",
    controller.signal
  )
    .catch((error) => {
      console.error("lookupStreetView() | error - ", error);
      return null;
    })
    .finally(() => clearTimeout(timeout));

  streetViewLookups.set(id, request);
  return request;
};

/**
 * Hook to resolve street views for each property in batch.
 */
export const usePropertyStreetViews = (properties: PropertyLike[]): PropertyImageMap => {
  const [images, setImages] = useState<PropertyImageMap>({});
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    const pending = properties.filter((property) => property && property.property_id);

    const resolve = async (property: PropertyLike) => {
      const id = property.property_id;
      const uri = (await lookupStreetView(property)) || null;
      if (!mounted.current) return;
      setImages((prev) => (prev[id] === uri ? prev : { ...prev, [id]: uri }));
    };

    const run = async () => {
      for (let index = 0; index < pending.length; index += STREET_VIEW_BATCH_SIZE) {
        if (!mounted.current) return;
        await Promise.all(pending.slice(index, index + STREET_VIEW_BATCH_SIZE).map(resolve));
      }
    };

    void run();
  }, [properties]);

  return images;
};
